<template>
  <div>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row>
        <v-col cols="12" md="4">
          <v-menu
            v-model="menus.dia"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            :disabled="isApproved"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="fields.dia"
                label="Dia"
                outlined
                dense
                readonly
                :disabled="isApproved"
                :rules="[rules.required]"
                v-bind="attrs"
                v-on="on"
              />
            </template>

            <v-date-picker
              v-model="fields.dia"
              :disabled="isApproved"
              @input="menus.dia = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="fields.created_to"
            :items="users"
            :item-text="item => item.funcionario_codigo + ' - ' + item.name"
            item-value="id"
            label="Colaborador"
            outlined
            dense
            :clearable="canManageSectionDiaries && !isApproved"
            :disabled="isApproved || !canManageSectionDiaries"
            :loading="loadingUsers"
            :rules="[rules.required]"
          />
        </v-col>

        <v-col cols="12" md="2">
          <v-text-field
            :value="selectedUserCodDepartamento"
            label="Departamento"
            outlined
            dense
            disabled
          />
        </v-col>
      </v-row>

      <v-row v-if="method === 'update'" class="mt-2">
        <v-col cols="12" md="4">
          <v-chip
            :color="isApproved ? 'success' : 'warning'"
            dark
            class="font-weight-bold"
          >
            <v-icon left small>
              {{ isApproved ? "mdi-check-circle" : "mdi-clock-outline" }}
            </v-icon>
            {{ isApproved ? "Aprovado" : "Por aprovar" }}
          </v-chip>
        </v-col>

        <v-col v-if="isApproved && approvedByText" cols="12" md="8">
          <div class="grey--text text--darken-1 pt-1">
            Aprovado por: <strong>{{ approvedByText }}</strong>
          </div>
        </v-col>
      </v-row>

      <v-alert
        v-if="isApproved"
        type="success"
        outlined
        class="mt-4"
      >
        Esta diária de secção já está aprovada e não pode ser alterada.
      </v-alert>

      <v-alert
        v-if="canApproveCurrentDiary"
        type="warning"
        outlined
        class="mt-4"
      >
        <div class="d-flex align-center justify-space-between">
          <span>Esta diária ainda está por aprovar.</span>

          <v-btn
            small
            color="success"
            dark
            :loading="approving"
            :disabled="approving || saving"
            @click="approveDiary"
          >
            <v-icon left>mdi-check-decagram</v-icon>
            Aprovar
          </v-btn>
        </div>
      </v-alert>

      <v-alert v-if="error" type="error" outlined class="mt-4">
        <div class="d-flex align-center justify-space-between">
          <span>{{ error }}</span>

          <v-btn
            v-if="existingDiaryId"
            small
            color="primary"
            class="ml-4"
            @click="openExistingDiary"
          >
            Abrir diária
          </v-btn>
        </div>
      </v-alert>

      <v-alert
        v-if="checkingDay"
        type="info"
        outlined
        class="mt-4"
      >
        A verificar diária...
      </v-alert>

      <template v-if="canShowLines">
        <v-divider class="my-4" />

        <div class="d-flex align-center justify-space-between mb-4">
          <div class="text-subtitle-1 font-weight-bold">
            Linhas da diária
          </div>

          <v-btn
            small
            color="primary"
            :disabled="isApproved"
            @click="addLinha"
          >
            <v-icon left>mdi-plus</v-icon>
            Adicionar linha
          </v-btn>
        </div>

        <v-card
          v-for="(linha, index) in fields.linhas"
          :key="index"
          outlined
          class="mb-4 diary-line-card"
        >
          <v-card-title class="py-3">
            <span class="text-subtitle-2 font-weight-bold">
              Linha {{ index + 1 }}
            </span>

            <v-spacer />

            <v-btn
              icon
              small
              color="error"
              :disabled="isApproved || fields.linhas.length === 1"
              @click="removeLinha(index)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="linha.horas"
                  label="Horas"
                  type="number"
                  outlined
                  dense
                  min="0"
                  step="0.25"
                  :disabled="isApproved"
                  :rules="[rules.required, rules.positive]"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="linha.id_centro_custo_destino"
                  :items="centrosCusto"
                  item-text="label"
                  item-value="id"
                  label="Centro custo destino"
                  outlined
                  dense
                  clearable
                  :disabled="isApproved"
                  :loading="loadingCentrosCusto"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="linha.id_equipamento"
                  :items="equipamentos"
                  :item-text="item => item.EntityKey + ' - ' + item.Descricao"
                  item-value="Id"
                  label="Equipamento"
                  outlined
                  dense
                  clearable
                  :disabled="isApproved"
                  :loading="loadingEquipamentos"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model="linha.horas_equipamento"
                  label="Horas equipamento"
                  type="number"
                  outlined
                  dense
                  min="0"
                  step="0.25"
                  :disabled="isApproved || !linha.id_equipamento"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="linha.notas"
                  label="Notas"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  :disabled="isApproved"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <v-row class="mt-6">
        <v-col cols="12" class="d-flex justify-end">
          <v-btn text class="mr-2" @click="$router.push('/section-diaries')">
            Cancelar
          </v-btn>

          <v-btn
            v-if="canApproveCurrentDiary"
            color="success"
            class="mr-2"
            :loading="approving"
            :disabled="approving || saving"
            @click="approveDiary"
          >
            <v-icon left>mdi-check-decagram</v-icon>
            Aprovar
          </v-btn>

          <v-btn
            color="success"
            :loading="saving"
            :disabled="saving || approving || !canShowLines || isApproved"
            @click="save"
          >
            <v-icon left>mdi-content-save</v-icon>
            Gravar
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import SectionDiariesAPI from "@/api/SectionDiaries.js";
import Obra from "@/api/Obra.js";
import Equipamentos from "@/api/Equipamentos.js";

export default {
  name: "SectionDiary",

  props: {
    method: {
      type: String,
      required: true,
    },
    sectionDiary: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    valid: false,
    saving: false,
    approving: false,
    error: null,
    checkingDay: false,
    dayAlreadyExists: false,
    existingDiaryId: null,

    loadingUsers: false,
    loadingCentrosCusto: false,
    loadingEquipamentos: false,

    users: [],
    centrosCusto: [],
    equipamentos: [],

    menus: {
      dia: false,
    },

    fields: {
      dia: null,
      created_to: null,
      linhas: [],
    },

    rules: {
      required: (v) => !!v || "Campo obrigatório.",
      positive: (v) => Number(v) >= 0 || "Valor inválido.",
    },
  }),

  computed: {
    isApproved() {
      return !!this.sectionDiary?.approved;
    },

    approvedByText() {
      const approver = this.sectionDiary?.approver;

      if (!approver) {
        return "";
      }

      if (approver.funcionario_codigo && approver.name) {
        return `${approver.funcionario_codigo} - ${approver.name}`;
      }

      return approver.name || approver.email || "";
    },

    selectedUser() {
      return this.users.find((user) => user.id === this.fields.created_to) || null;
    },

    selectedUserCodDepartamento() {
      return this.selectedUser?.CodDepartamento || "";
    },

    canManageSectionDiaries() {
      const user = this.$store.state.user.data;
      const permissions = user?.permissions || [];

      return (
        user?.is_super ||
        permissions.some(
          (permission) => permission.name === "section_diaries.manage"
        )
      );
    },

    canApproveSectionDiaries() {
      const user = this.$store.state.user.data;
      const permissions = user?.permissions || [];

      return (
        user?.is_super ||
        permissions.some(
          (permission) => permission.name === "section_diaries.manage"
        )
      );
    },

    canApproveCurrentDiary() {
      return (
        this.method === "update" &&
        this.sectionDiary?.id &&
        !this.isApproved &&
        this.canApproveSectionDiaries
      );
    },

    currentFuncionarioCodigo() {
      return this.$store.state.user.data?.funcionario?.Codigo || null;
    },

    canShowLines() {
      return (
        this.fields.dia &&
        this.fields.created_to &&
        !this.dayAlreadyExists &&
        !this.checkingDay
      );
    },
  },

  watch: {
    "fields.dia"() {
      this.onHeaderChanged();
    },

    "fields.created_to"() {
      this.onHeaderChanged();
    },
  },

  mounted() {
    this.loadBaseData();

    if (this.method === "update" && this.sectionDiary) {
      this.fillFields();
    } else {
      this.fields.dia = new Date().toISOString().substr(0, 10);
    }
  },

  methods: {
    openExistingDiary() {
      if (!this.existingDiaryId) {
        return;
      }

      this.$router.push(`/section-diaries/${this.existingDiaryId}`);
    },

    async loadBaseData() {
      try {
        this.loadingUsers = true;
        this.loadingCentrosCusto = true;
        this.loadingEquipamentos = true;

        const [usersResp, centrosResp, equipamentosResp] = await Promise.all([
          SectionDiariesAPI.getUsersList(),
          Obra.all({ tipo: "O" }),
          Equipamentos.all(),
        ]);

        this.users = usersResp?.data?.conduril || usersResp?.data || [];

        this.centrosCusto = (centrosResp?.data || []).map((obra) => ({
          id: obra.Id,
          label: `${obra.Codigo} - ${obra.Descricao}`,
        }));

        this.equipamentos = equipamentosResp?.data || [];

        this.setDefaultCreatedTo();
      } catch (e) {
        console.error(e);
        this.error = "Erro ao carregar dados base.";
      } finally {
        this.loadingUsers = false;
        this.loadingCentrosCusto = false;
        this.loadingEquipamentos = false;
      }
    },

    setDefaultCreatedTo() {
      if (this.canManageSectionDiaries && this.method === "create") {
        return;
      }

      const user = this.users.find(
        (u) => u.funcionario_codigo == this.currentFuncionarioCodigo
      );

      if (user && !this.canManageSectionDiaries) {
        this.fields.created_to = user.id;
      }
    },

    async onHeaderChanged() {
      if (this.isApproved) {
        return;
      }

      this.error = null;
      this.dayAlreadyExists = false;
      this.existingDiaryId = null;

      if (this.method === "update") {
        return;
      }

      this.fields.linhas = [];

      if (!this.fields.dia || !this.fields.created_to) {
        return;
      }

      await this.checkDay();

      if (!this.dayAlreadyExists && !this.fields.linhas.length) {
        this.addLinha();
      }
    },

    async checkDay() {
      try {
        this.checkingDay = true;

        const response = await SectionDiariesAPI.checkDay(
          this.fields.created_to,
          this.fields.dia
        );

        const existingDiaryId = response?.data || null;

        this.existingDiaryId = existingDiaryId;
        this.dayAlreadyExists = !!existingDiaryId;

        if (this.dayAlreadyExists) {
          this.fields.linhas = [];
          this.error =
            "Para esse colaborador e para esse dia já existe uma diária.";
        }
      } catch (e) {
        console.error(e);
        this.error = "Erro ao verificar se já existe diária para esse dia.";
        this.dayAlreadyExists = true;
        this.existingDiaryId = null;
      } finally {
        this.checkingDay = false;
      }
    },

    fillFields() {
      this.fields = {
        dia: this.sectionDiary.dia,
        created_to: this.sectionDiary.created_to,
        linhas: (this.sectionDiary.linhas || []).map((linha) => ({
          horas: linha.horas,
          id_centro_custo_destino: linha.id_centro_custo_destino,
          notas: linha.notas,
          id_equipamento: linha.id_equipamento,
          horas_equipamento: linha.horas_equipamento,
        })),
      };

      if (!this.fields.linhas.length && !this.isApproved) {
        this.addLinha();
      }
    },

    emptyLinha() {
      return {
        horas: null,
        id_centro_custo_destino: null,
        notas: null,
        id_equipamento: null,
        horas_equipamento: null,
      };
    },

    addLinha() {
      if (this.isApproved) {
        return;
      }

      this.fields.linhas.push(this.emptyLinha());
    },

    removeLinha(index) {
      if (this.isApproved) {
        return;
      }

      this.fields.linhas.splice(index, 1);
    },

    buildPayload() {
      return {
        dia: this.fields.dia,
        created_to: this.fields.created_to,

        linhas: this.fields.linhas.map((linha) => ({
          user_id: this.fields.created_to,
          horas: linha.horas,
          id_centro_custo_destino: linha.id_centro_custo_destino,
          notas: linha.notas,
          id_equipamento: linha.id_equipamento,
          horas_equipamento: linha.id_equipamento
            ? linha.horas_equipamento
            : null,
        })),
      };
    },

    async approveDiary() {
      this.error = null;

      if (!this.canApproveCurrentDiary) {
        return;
      }

      try {
        this.approving = true;

        await SectionDiariesAPI.approve(this.sectionDiary.id);

        this.$router.push("/section-diaries");
      } catch (e) {
        console.error(e);
        this.error = "Erro ao aprovar diária de secção.";
      } finally {
        this.approving = false;
      }
    },

    async save() {
      this.error = null;

      if (this.isApproved) {
        this.error = "Esta diária de secção já está aprovada e não pode ser alterada.";
        return;
      }

      if (!this.canShowLines) {
        this.error = "Escolhe o dia e o colaborador antes de gravar.";
        return;
      }

      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.saving = true;

        const payload = this.buildPayload();

        if (this.method === "create") {
          await SectionDiariesAPI.create(payload);
        } else {
          await SectionDiariesAPI.update(payload, this.sectionDiary.id);
        }

        this.$router.push("/section-diaries");
      } catch (e) {
        console.error(e);
        this.error = "Erro ao gravar diária de secção.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.diary-line-card {
  border-radius: 14px;
}
</style>